import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { api } from '../services/api';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('es-MX', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function formatDuration(seconds: number): string {
  if (!seconds) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function CallsPage() {
  const queryClient = useQueryClient();

  const { data: queueStats } = useQuery({
    queryKey: ['callQueue'],
    queryFn: () => api.getCallQueueStats(),
    refetchInterval: 10000,
  });

  const { data: calls, isLoading } = useQuery({
    queryKey: ['callLogs'],
    queryFn: () => api.getCallLogs(50),
    refetchInterval: 30000,
  });

  const processQueueMutation = useMutation({
    mutationFn: () => api.processCallQueue(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQueue'] });
      queryClient.invalidateQueries({ queryKey: ['callLogs'] });
    },
  });

  const statusLabels: Record<string, { label: string; class: string }> = {
    pending: { label: 'Pendiente', class: 'badge-warning' },
    processing: { label: 'En proceso', class: 'badge-primary' },
    completed: { label: 'Completada', class: 'badge-success' },
    failed: { label: 'Fallida', class: 'badge-danger' },
    no_answer: { label: 'Sin respuesta', class: 'badge-danger' },
    busy: { label: 'Ocupado', class: 'badge-warning' },
  };

  const getStatusInfo = (status: string) => {
    return statusLabels[status] || { label: status, class: 'badge-warning' };
  };

  return (
    <div>
      <Header title="Llamadas IA" />

      {/* Queue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <div className="text-2xl mb-2">📞</div>
          <div className="stat-value">{queueStats?.pending || 0}</div>
          <div className="stat-label">En cola</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">🔄</div>
          <div className="stat-value">{queueStats?.processing || 0}</div>
          <div className="stat-label">Procesando</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">✅</div>
          <div className="stat-value">{queueStats?.completed_today || 0}</div>
          <div className="stat-label">Completadas hoy</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">❌</div>
          <div className="text-3xl font-bold text-red-400">{queueStats?.failed_today || 0}</div>
          <div className="stat-label">Fallidas hoy</div>
        </div>
      </div>

      {/* Actions */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-primary-500">Cola de Llamadas</h2>
            <p className="text-sm text-gray-500">
              {queueStats?.pending || 0} llamadas pendientes en la cola
            </p>
          </div>
          <button
            onClick={() => processQueueMutation.mutate()}
            className="btn btn-primary"
            disabled={processQueueMutation.isPending || !queueStats?.pending}
          >
            {processQueueMutation.isPending ? '🔄 Procesando...' : '▶️ Procesar Cola'}
          </button>
        </div>
      </div>

      {/* Call Logs */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">📝 Historial de Llamadas</h2>
        {isLoading ? (
          <p className="text-gray-500 text-center py-4">Cargando...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b border-dark-border">
                  <th className="pb-3 font-medium">Fecha</th>
                  <th className="pb-3 font-medium">Teléfono</th>
                  <th className="pb-3 font-medium">Tipo</th>
                  <th className="pb-3 font-medium">Estado</th>
                  <th className="pb-3 font-medium">Duración</th>
                  <th className="pb-3 font-medium">Resumen</th>
                </tr>
              </thead>
              <tbody>
                {calls?.map((call) => {
                  const statusInfo = getStatusInfo(call.status);
                  return (
                    <tr
                      key={call.id}
                      className="border-b border-dark-border/50 hover:bg-dark-border/30"
                    >
                      <td className="py-3 text-sm">{formatDate(call.created_at)}</td>
                      <td className="py-3 font-mono">{call.phone_number}</td>
                      <td className="py-3">
                        <span className="text-sm">
                          {call.call_type === 'reminder' && '🔔 Recordatorio'}
                          {call.call_type === 'collection' && '💰 Cobranza'}
                          {call.call_type === 'notification' && '📢 Notificación'}
                          {call.call_type === 'custom' && '📞 Personalizada'}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`badge ${statusInfo.class}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="py-3 text-sm">{formatDuration(call.duration || 0)}</td>
                      <td className="py-3 text-sm text-gray-400 max-w-xs truncate">
                        {call.ai_summary || '—'}
                      </td>
                    </tr>
                  );
                })}
                {!calls?.length && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No hay llamadas registradas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
